import React, { useState } from 'react';
import { useDelete, useGetList, useNotify, useUpdate } from 'react-admin';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

import { invitePartnerMember } from '../../Services/partners';

// Utilisateurs d'une organisation partenaire, vus depuis l'admin Quanti : liste, invitation
// (l'invité reçoit un email et doit l'accepter dans l'espace partenaire pour que son accès
// s'ouvre) et retrait. Même API que la surface partenaire, ressource react-admin
// `partner-members` → api-partners /v1/admin.

export type PartnerMember = {
	id: number;
	email: string;
	firstName?: string;
	lastName?: string;
	role: string;
	roleInOrg?: string;
	job?: string;
	userId?: number;
};

// Rôles d'ACCÈS applicatif — à ne pas confondre avec `roleInOrg`, qui est le titre de la
// personne dans son entreprise (« Gérant ») et n'ouvre aucun droit.
//
// `staff` ne s'accorde QUE depuis l'admin : c'est le rôle Quanti interne, qui ouvre les vues
// cross-partenaire. La surface partenaire le refuse explicitement.
const ROLE_CHOICES = [
	{
		id: 'owner',
		name: 'Owner',
		help: "Administre l'organisation (utilisateurs, bancaire, profil) et voit tous les clients.",
	},
	{
		id: 'manager',
		name: 'Manager',
		help: 'Tous les clients + gestion des utilisateurs, sans les réglages sensibles.',
	},
	{
		id: 'collaborator',
		name: 'Collaborator',
		help: 'Accès uniquement aux clients qui lui sont assignés.',
	},
	{
		id: 'staff',
		name: 'Staff (Quanti)',
		help: "Collaborateur Quanti : droits d'affichage élargis sur TOUS les partenaires. À réserver à l'interne.",
	},
];

const roleLabel = (role: string) => ROLE_CHOICES.find((c) => c.id === role)?.name || role || '—';

const EMPTY_FORM = { firstName: '', lastName: '', email: '', role: 'collaborator' };

// #region InviteMemberDialog — invitation d'un utilisateur dans l'organisation
const InviteMemberDialog: React.FC<{
	partnerId: string;
	open: boolean;
	onClose: () => void;
	onInvited: () => void;
}> = ({ partnerId, open, onClose, onInvited }) => {
	const notify = useNotify();
	const [form, setForm] = useState(EMPTY_FORM);
	const [saving, setSaving] = useState(false);

	const set = (key: keyof typeof EMPTY_FORM) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setForm((f) => ({ ...f, [key]: e.target.value }));

	const close = () => {
		setForm(EMPTY_FORM);
		onClose();
	};

	const submit = async () => {
		setSaving(true);
		try {
			const created = await invitePartnerMember({ ...form, email: form.email.trim(), partnerId });
			// Le membre est créé dans les deux cas — mais annoncer « invitation envoyée »
			// quand l'email n'est pas parti laisse attendre la réaction de quelqu'un qui n'a
			// jamais été prévenu. Le distinguer est tout l'intérêt de `emailSent`.
			if (created.emailSent) {
				notify("Invitation envoyée — l'accès s'ouvrira quand elle sera acceptée", { type: 'success' });
			} else {
				notify(
					`Membre ajouté, mais l'email d'invitation n'est PAS parti (${created.email}). Il faut le prévenir autrement.`,
					{ type: 'warning', autoHideDuration: 12000 }
				);
			}
			close();
			onInvited();
		} catch (e: any) {
			// Le back refuse notamment un email déjà membre de CE partenaire : son message
			// est utile, on le montre tel quel plutôt qu'un « erreur » muet.
			notify(`Erreur : ${e?.message || e}`, { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const roleHelp = ROLE_CHOICES.find((c) => c.id === form.role)?.help;

	return (
		<Dialog open={open} onClose={close} fullWidth maxWidth="xs">
			<DialogTitle>Inviter un utilisateur</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<Stack direction="row" spacing={2}>
						<TextField label="Prénom" value={form.firstName} onChange={set('firstName')} fullWidth />
						<TextField label="Nom" value={form.lastName} onChange={set('lastName')} fullWidth />
					</Stack>
					<TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth />
					<TextField select label="Rôle d'accès" value={form.role} onChange={set('role')} helperText={roleHelp} fullWidth>
						{ROLE_CHOICES.map((c) => (
							<MenuItem key={c.id} value={c.id}>
								{c.name}
							</MenuItem>
						))}
					</TextField>
					<Typography variant="caption" color="text.secondary">
						Un email d'invitation est envoyé (tu seras averti s'il ne part pas). Si l'adresse
						correspond déjà à un compte Quanti, l'adhésion est active sans attendre.
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={close} color="inherit">
					Annuler
				</Button>
				<Button variant="contained" disabled={!form.email.trim() || saving} onClick={submit}>
					Inviter
				</Button>
			</DialogActions>
		</Dialog>
	);
};
// #endregion

// #region MemberRow — une ligne du tableau (nom, accès modifiable, statut, retrait)
const MemberRow: React.FC<{
	member: PartnerMember;
	saving: boolean;
	onRoleChange: (m: PartnerMember, role: string) => void;
	onRemove: (m: PartnerMember) => void;
}> = ({ member, saving, onRoleChange, onRemove }) => {
	const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
	// user_id = 0 → invitation créée par email, pas encore acceptée : AUCUN accès pour
	// l'instant. Distinction essentielle ici, sinon un admin croit avoir ouvert un accès.
	const active = !!member.userId;
	const knownRole = ROLE_CHOICES.some((c) => c.id === member.role);

	return (
		<TableRow>
			<TableCell>{fullName || '—'}</TableCell>
			<TableCell>{member.email}</TableCell>
			<TableCell>
				{/* Rôle modifiable sur place : sinon corriger une faute de frappe imposait de
				    supprimer et réinviter, donc de renvoyer un email à l'intéressé. `value`
				    vient des données, pas d'un state local — en mode pessimiste l'affichage
				    ne bouge qu'une fois l'API d'accord. */}
				<Tooltip title={ROLE_CHOICES.find((c) => c.id === member.role)?.help || ''}>
					<TextField
						select
						size="small"
						variant="standard"
						value={knownRole ? member.role : ''}
						disabled={saving}
						onChange={(e) => onRoleChange(member, e.target.value)}
						sx={{ minWidth: 150 }}
					>
						{/* Un rôle hors liste (donnée héritée, ex. l'ancien `member`) doit rester
						    lisible plutôt que d'afficher un select vide. */}
						{!knownRole && (
							<MenuItem value="" disabled>
								{member.role || '—'}
							</MenuItem>
						)}
						{ROLE_CHOICES.map((c) => (
							<MenuItem key={c.id} value={c.id}>
								{c.name}
							</MenuItem>
						))}
					</TextField>
				</Tooltip>
			</TableCell>
			<TableCell>{member.job || member.roleInOrg || '—'}</TableCell>
			<TableCell>
				<Chip
					label={active ? 'Actif' : 'Invitation en attente'}
					size="small"
					color={active ? 'success' : 'default'}
					variant={active ? 'filled' : 'outlined'}
				/>
			</TableCell>
			<TableCell align="right">
				<Tooltip title="Retirer de l'organisation">
					<IconButton size="small" aria-label="retirer" onClick={() => onRemove(member)}>
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</TableCell>
		</TableRow>
	);
};
// #endregion

// #region PartnerMembers — carte « Utilisateurs liés » avec invitation et retrait
export const PartnerMembers: React.FC<{ partnerId: string }> = ({ partnerId }) => {
	const notify = useNotify();
	const [inviteOpen, setInviteOpen] = useState(false);
	const [remove] = useDelete();
	const [update] = useUpdate();
	const [savingId, setSavingId] = useState<number | null>(null);

	const { data: members, refetch } = useGetList<any>('partner-members', {
		pagination: { page: 1, perPage: 100 },
		sort: { field: 'id', order: 'ASC' },
		filter: { partner_id: partnerId },
	});

	const onRoleChange = (member: PartnerMember, role: string) => {
		if (!role || role === member.role) return;
		setSavingId(member.id);
		update(
			'partner-members',
			{ id: member.id, data: { role }, previousData: member },
			{
				// Pessimiste : le rôle affiché doit refléter la base, pas l'intention.
				mutationMode: 'pessimistic',
				onSuccess: () => {
					setSavingId(null);
					notify(`Rôle mis à jour : ${roleLabel(role)}`, { type: 'success' });
					refetch();
				},
				onError: (e: any) => {
					setSavingId(null);
					notify(`Erreur : ${e?.message || e}`, { type: 'error' });
					refetch();
				},
			}
		);
	};

	const onRemove = (member: PartnerMember) => {
		if (!window.confirm(`Retirer ${member.email} de cette organisation ?`)) return;
		remove(
			'partner-members',
			{ id: member.id, previousData: member },
			{
				// Pessimiste : on ne veut pas afficher un retrait qui n'a pas eu lieu côté API.
				mutationMode: 'pessimistic',
				onSuccess: () => {
					notify('Utilisateur retiré', { type: 'success' });
					refetch();
				},
				onError: (e: any) => notify(`Erreur : ${e?.message || e}`, { type: 'error' }),
			}
		);
	};

	return (
		<Card>
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
					<Typography variant="h6">Utilisateurs liés</Typography>
					<Button size="small" variant="contained" startIcon={<PersonAddAltIcon />} onClick={() => setInviteOpen(true)}>
						Inviter un utilisateur
					</Button>
				</Box>

				{!members || members.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Aucun utilisateur rattaché à ce partenaire.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Nom</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Accès</TableCell>
								<TableCell>Fonction</TableCell>
								<TableCell>Statut</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{members.map((m: any) => (
								<MemberRow
									key={m.id}
									member={m}
									saving={savingId === m.id}
									onRoleChange={onRoleChange}
									onRemove={onRemove}
								/>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>

			<InviteMemberDialog
				partnerId={partnerId}
				open={inviteOpen}
				onClose={() => setInviteOpen(false)}
				onInvited={refetch}
			/>
		</Card>
	);
};
// #endregion
